import { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  writeBatch, 
  doc, 
  Timestamp, 
  addDoc, 
  limit, // <-- New import
} from 'firebase/firestore'; 

// Utility function to make the notification type user-friendly
const formatNotificationTitle = (type) => {
    if (!type) return 'New Update';
    // Example mapping based on common notification types
    const mapping = {
        'NEW_MESSAGE': 'Nova mensagem',
        'TRADE_UPDATE': 'Status da Troca Atualizado',
        'ITEM_LIKED': 'Item Liked',
        'DISPUTE': 'Alerta de Disputa'
    };
    return mapping[type] || type.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

/**
 * A comprehensive hook to manage all user notifications in real-time.
 * It provides grouped data, stats, and actions to manage notifications.
 * @param {object} db - The Firestore database instance.
 * @param {string} userId - The ID of the current user.
 * @returns {object} - An object containing notifications, loading state, grouped data, stats, and action functions.
 */
export const useSmartNotifications = (db, userId) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !db) {
      setLoading(false);
      console.log('[useSmartNotifications] Hook skipped: Missing DB or User ID.');
      return;
    }

    console.log(`[useSmartNotifications] Query running for User ID: ${userId}`);

    // Use the legacy Firestore path confirmed by the database screenshot: /users/{userId}/notifications
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    
    // Listen to RECENT notifications for the bell dropdown (limiting to 20)
    const q = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(10) // <<< CRITICAL FIX: Limit the query size for performance
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log(`[useSmartNotifications] Snapshot returned ${snapshot.size} total documents (limited to 10).`);
      try {
        const newNotifications = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Use 'createdAt' from the database
            createdAt: data.createdAt?.toDate() || new Date(),
            // Use 'isRead' from the database and ensure it's a strict boolean (FIX for badge)
            isRead: !!data.isRead,
            // --- FIX: Use type for title, message for body (FIX for content) ---
            title: formatNotificationTitle(data.type) || data.title, // Use formatted type as title
            body: data.message || data.body || '', // Use message field for body/detail text
            // ---------------------------------------------------------------------------------
          };
        });
        setNotifications(newNotifications);
        
        const unreadCountCheck = newNotifications.filter(n => !n.isRead).length;
        console.log(`[useSmartNotifications] Unread notifications found: ${unreadCountCheck}`);

        setError(null);
      } catch (err) {
        console.error('Error processing notification snapshot:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }, (err) => {
      console.error('[useSmartNotifications] Error listening to notifications:', err);
      setError(err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db, userId]);

  // Memoized grouping of notifications for efficient access
  const groupedNotifications = useMemo(() => {
    const groups = { byTab: {}, byItem: {}, byTrade: {} };
    
    notifications.forEach(notification => {
      // Group by tab category
      const tab = notification.tabCategory || 'general';
      if (!groups.byTab[tab]) groups.byTab[tab] = [];
      groups.byTab[tab].push(notification);

      // Group by item ID
      const itemId = notification.metadata?.itemId || notification.entityId;
      if (itemId) {
        if (!groups.byItem[itemId]) groups.byItem[itemId] = [];
        groups.byItem[itemId].push(notification);
      }

      // Group by trade ID
      const tradeId = notification.metadata?.tradeId || notification.tradeId;
      if (tradeId) {
        if (!groups.byTrade[tradeId]) groups.byTrade[tradeId] = [];
        groups.byTrade[tradeId].push(notification);
      }
    });

    return groups;
  }, [notifications]);

  // Memoized statistics about the notifications
  const stats = useMemo(() => {
    const unreadCount = notifications.filter(n => !n.isRead).length;

    return {
      total: notifications.length,
      unreadCount: unreadCount,
      byType: {
        urgent: notifications.filter(n => n.type === 'urgent').length,
        warning: notifications.filter(n => n.type === 'warning').length,
        success: notifications.filter(n => n.type === 'success').length,
        info: notifications.filter(n => n.type === 'info').length,
      },
      byPriority: {
        high: notifications.filter(n => n.priority === 'high').length,
        medium: notifications.filter(n => n.priority === 'medium').length,
        low: notifications.filter(n => n.priority === 'low').length,
      },
    };
  }, [notifications]);

  // Action to mark one or more notifications as read
  const markAsRead = useCallback(async (notificationIds) => {
    if (!notificationIds || notificationIds.length === 0) return;
    
    const ids = Array.isArray(notificationIds) ? notificationIds : [notificationIds];
    const batch = writeBatch(db);
    
    ids.forEach(id => {
      // Use the legacy Firestore path
      const notificationRef = doc(db, 'users', userId, 'notifications', id);
      batch.update(notificationRef, { isRead: true, readAt: Timestamp.now() }); // Use Timestamp.now()
    });

    try {
      await batch.commit();
      console.log(`[useSmartNotifications] Successfully marked ${ids.length} notification(s) as read.`);
    } catch (err) {
      console.error('[useSmartNotifications] Error marking notifications as read:', err);
      throw err;
    }
  }, [db, userId]);

  // Action to mark all notifications in a specific tab as read
  const markTabAsRead = useCallback(async (tabCategory) => {
    const notificationIds = (groupedNotifications.byTab[tabCategory] || []).map(n => n.id);
    if (notificationIds.length > 0) {
      await markAsRead(notificationIds);
    }
  }, [groupedNotifications.byTab, markAsRead]);

  // Action to create a new notification for a specific user
  const createNotification = useCallback(async (notificationData) => {
    // The recipientId should be part of notificationData, otherwise it defaults to the current user
    const recipientId = notificationData.recipientId || userId;
    const dataToSend = { ...notificationData };
    delete dataToSend.recipientId; // Clean up recipientId before saving to Firestore

    try {
      // Use the legacy Firestore path
      const notificationsRef = collection(db, 'users', recipientId, 'notifications');
      await addDoc(notificationsRef, {
        isRead: false,
        createdAt: new Date(),
        type: 'info',
        priority: 'medium',
        context: 'general',
        ...dataToSend,
      });
      console.log(`[useSmartNotifications] Successfully created notification for recipient: ${recipientId}`);
    } catch (err) {
      console.error('[useSmartNotifications] Error creating notification:', err);
      throw err;
    }
  }, [db, userId]); // userId is a fallback if recipientId is not provided

  return {
    notifications,
    loading,
    error,
    groupedNotifications,
    stats,
    markAsRead,
    markTabAsRead,
    createNotification,
    userId, // Expose userId if needed by components
  };
};


/**
 * A specific hook to manage notifications for a single tab category.
 * It is more efficient as it only listens to relevant unread notifications.
 * @param {object} db - The Firestore database instance.
 * @param {string} userId - The ID of the current user.
 * @param {string} tabCategory - The specific tab category to listen for.
 * @returns {object} - An object containing tab-specific notifications and derived state.
 */
export const useTabNotifications = (db, userId, tabCategory) => {
  const [notifications, setNotifications] = useState([]); 
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !db || !tabCategory) {
        setLoading(false);
        console.log('[useTabNotifications] Hook skipped: Missing DB, User ID, or Tab Category.');
        return;
    }

    console.log(`[useTabNotifications] Query running for Tab: ${tabCategory} | User ID: ${userId}`);


    // Use the legacy Firestore path confirmed by the database screenshot: /users/{userId}/notifications
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(
      notificationsRef,
      where('isRead', '==', false),
      where('tabCategory', '==', tabCategory),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log(`[useTabNotifications] Snapshot returned ${snapshot.size} documents for tab ${tabCategory}.`);
      const tabNotifications = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          isRead: !!data.isRead,
          // --- FIX: Use type for title, message for body ---
          title: formatNotificationTitle(data.type) || data.title, 
          body: data.message || data.body || '', // Use message field for body/detail text
          // ---------------------------------------------------------------------------------
        };
      });
      
      setNotifications(tabNotifications); 
      setLoading(false);
    }, (error) => {
        console.error(`[useTabNotifications] Error loading notifications for tab ${tabCategory}:`, error);
        setLoading(false);
    });

    return () => unsubscribe();
  }, [db, userId, tabCategory]);

  const markAsRead = useCallback(async () => {
    if (notifications.length === 0) return;
    const batch = writeBatch(db);
    notifications.forEach(n => {
        // Use the legacy Firestore path
        const notifRef = doc(db, 'users', userId, 'notifications', n.id);
        batch.update(notifRef, { isRead: true, readAt: Timestamp.now() }); // Use Timestamp.now()
    });
    try {
        await batch.commit();
        console.log(`[useTabNotifications] Successfully marked ${notifications.length} item(s) in tab ${tabCategory} as read.`);
    } catch (error) {
        console.error(`[useTabNotifications] Error marking notifications for tab ${tabCategory} as read:`, error);
    }
  }, [db, userId, notifications]);

  const criticalType = useMemo(() => {
    if (notifications.some(n => n.type === 'urgent')) return 'urgent';
    if (notifications.some(n => n.type === 'warning')) return 'warning';
    if (notifications.some(n => n.type === 'success')) return 'success';
    if (notifications.some(n => n.type === 'info')) return 'info';
    return null;
  }, [notifications]);

  const highestPriority = useMemo(() => {
    if (notifications.some(n => n.priority === 'high')) return 'high';
    if (notifications.some(n => n.priority === 'medium')) return 'medium';
    if (notifications.some(n => n.priority === 'low')) return 'low';
    return null;
  }, [notifications]);

  return {
    notifications,
    loading,
    hasNotifications: notifications.length > 0,
    count: notifications.length,
    type: criticalType,
    priority: highestPriority,
    markAsRead,
  };
};
